// Creator mode on the web: the requests (signed-in session; the open list is public), the account's tier and what it
// allows, the words for it, the results as CSV, and the host keys this device remembers for the battles it made.
// No email ever reaches these pages: every row is read field by field and only the listed fields are kept.
import type {
  AccountPlan, BattleResultsRow, BattleStatus, CreatorBattleRow, CreatorLimits, CreatorTier, OpenBattleRow, QuickBattleRequest,
  QuickBattleResponse, StaffRole, StudioCatalogue,
} from '@shared/types';
import { CREATOR_LIMITS, QUICK_LENGTHS } from '@shared/types';
import type { PoolStage } from '@shared/pool';
import { api } from '@/lib/api';
import { staffApi } from '@/staff/auth';

export const MAX_TITLE = 60;
/** The server's limit on a quick battle's host name (MAX_HOST_NAME in api/src/handlers/creator.ts). */
export const MAX_HOST_NAME = 40;
/** 17 Oct 2026: the account's name fills the host name box, never an email address standing in for a name. */
export const hostNameFor = (name: string) => (/\S+@\S+\.\S+/.test(name) ? '' : name.trim().slice(0, MAX_HOST_NAME));
export type QuickLength = keyof typeof QUICK_LENGTHS;
export const LENGTHS: readonly QuickLength[] = ['short', 'standard', 'long'];
const STATUSES: readonly BattleStatus[] = ['lobby', 'question', 'reveal', 'podium', 'ended'];

// ---------------------------------------------------------------------------
// The tier: creator (or staff, admin or manager: 18 Oct 2026), then teacher, then a paying member, else free. Mirrors the API's
// tierOf. Roles and plan only raise limits.
// ---------------------------------------------------------------------------
export function tierFor(roles: readonly StaffRole[] | undefined, plan?: AccountPlan): CreatorTier {
  if (roles?.some((r) => r === 'creator' || r === 'staff' || r === 'admin' || r === 'manager')) return 'creator';
  if (roles?.includes('teacher')) return 'teacher';
  return plan === 'member' ? 'member' : 'free';
}
export const TIER_LABEL: Record<CreatorTier, string> = { free: 'Free account', member: 'Member', teacher: 'Teacher', creator: 'Creator' };
const n = (x: number) => x.toLocaleString('en-GB');
/** "Free account: 10 battles a day, 1 live at a time, up to 30 players and 15 questions." */
export function tierLine(tier: CreatorTier, limits: CreatorLimits = CREATOR_LIMITS[tier]): string {
  const live = limits.liveAtOnce === 1 ? '1 live at a time' : `${n(limits.liveAtOnce)} live at once`;
  const day = `${n(limits.battlesPerDay)} ${limits.battlesPerDay === 1 ? 'battle' : 'battles'} a day`;
  const extra = tier === 'teacher' ? ' Results for each player afterwards.' : '';
  return `${TIER_LABEL[tier]}: ${day}, ${live}, up to ${n(limits.maxPlayers)} players and ${n(limits.maxQuestions)} questions.${extra}`;
}
/** Questions a length deals for this tier (the tier's ceiling caps the long one). */
export const questionsFor = (length: QuickLength, tier: CreatorTier) => Math.min(QUICK_LENGTHS[length], CREATOR_LIMITS[tier].maxQuestions);
export const LENGTH_LABEL: Record<QuickLength, string> = { short: 'Short', standard: 'Standard', long: 'Long' };
/** Teachers see each player's results afterwards; MedSnapp staff can too. */
export const canSeeResults = (roles: readonly StaffRole[] | undefined) => Boolean(roles?.some((r) => r === 'teacher' || r === 'admin' || r === 'staff'));

// ---------------------------------------------------------------------------
// Rows, read field by field (anything else the server sends, an email above all, is dropped here)
// ---------------------------------------------------------------------------
type Raw = Record<string, unknown>;
const obj = (x: unknown): Raw | undefined => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Raw) : undefined);
const str = (x: unknown) => (typeof x === 'string' ? x : undefined);
const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : undefined);
const status = (x: unknown): BattleStatus => (STATUSES.includes(x as BattleStatus) ? (x as BattleStatus) : 'lobby');
const vis = (x: unknown): 'private' | 'open' => (x === 'open' ? 'open' : 'private');
function listIn(body: unknown, ...names: string[]): unknown[] {
  if (Array.isArray(body)) return body;
  const o = obj(body);
  if (o) for (const k of names) if (Array.isArray(o[k])) return o[k] as unknown[];
  return [];
}

export function creatorRow(raw: unknown): CreatorBattleRow | undefined {
  const r = obj(raw); const code = str(r?.code);
  if (!r || !code) return undefined;
  const at = num(r.scheduledStartAt);
  return { code, title: str(r.title) ?? code, status: status(r.status), playerCount: num(r.playerCount) ?? 0, createdAt: num(r.createdAt) ?? 0, visibility: vis(r.visibility), ...(at ? { scheduledStartAt: at } : {}) };
}
export function openRow(raw: unknown): OpenBattleRow | undefined {
  const r = obj(raw); const code = str(r?.code);
  if (!r || !code) return undefined;
  const host = str(r.hostName)?.trim(); const at = num(r.scheduledStartAt);
  return {
    code, title: str(r.title) ?? code, status: status(r.status), playerCount: num(r.playerCount) ?? 0,
    categories: Array.isArray(r.categories) ? r.categories.filter((c): c is string => typeof c === 'string') : [],
    ...(host ? { hostName: host } : {}), ...(at ? { scheduledStartAt: at } : {}),
  };
}
export function resultsRow(raw: unknown): BattleResultsRow | undefined {
  const r = obj(raw); const name = str(r?.name);
  if (!r || name === undefined) return undefined;
  const school = str(r.medSchool)?.trim(); const year = str(r.year)?.trim() ?? (num(r.year) !== undefined ? String(r.year) : undefined);
  return {
    name, rank: num(r.rank) ?? 0, score: num(r.score) ?? 0, correct: num(r.correct) ?? 0, answered: num(r.answered) ?? 0,
    ...(school ? { medSchool: school } : {}), ...(year ? { year } : {}),
  };
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
export interface Account { tier: CreatorTier; plan: AccountPlan; suspended: boolean; roles: StaffRole[] }
/** A category Make a battle offers, with its level (17 Oct 2026). */
export interface LevelCategory { category: string; stage: PoolStage }
/** A catalogue row's level: as sent, else Year 1–2 for the Preclinical domain, else clinical. */
const stageIn = (r: Raw): PoolStage => (r.stage === 'preclinical' || r.stage === 'clinical' ? r.stage : r.domain === 'Preclinical' ? 'preclinical' : 'clinical');

/** The signed-in account's requests. A session the server no longer takes is forgotten (staffApi). */
export function creatorClient(session: string) {
  const auth = { session };
  return {
    /** GET /auth/me as it is now, for the plan and a suspension (the session itself carries only the roles). */
    account: async (fallbackRoles: StaffRole[]): Promise<Account> => {
      const r = obj(await staffApi<unknown>('/auth/me', auth)) ?? {};
      const roles = Array.isArray(r.roles) ? (r.roles as StaffRole[]) : fallbackRoles;
      const plan: AccountPlan = r.plan === 'member' ? 'member' : 'free';
      return { tier: tierFor(roles, plan), plan, suspended: r.suspended === true, roles };
    },
    /** The categories to choose from (the checked pool's catalogue), each with its level, by name. */
    categories: async (): Promise<LevelCategory[]> => {
      const c = await staffApi<Partial<StudioCatalogue>>('/studio/catalogue', auth);
      const out = new Map<string, LevelCategory>();
      for (const x of Array.isArray(c.categories) ? c.categories : []) {
        const r = obj(x); const category = str(r?.category);
        if (r && category && !out.has(category)) out.set(category, { category, stage: stageIn(r) });
      }
      return [...out.values()].sort((a, b) => a.category.localeCompare(b.category, 'en-GB'));
    },
    create: (req: QuickBattleRequest) => staffApi<QuickBattleResponse>('/creator/battles', auth, { method: 'POST', body: JSON.stringify(req) }),
    mine: async (): Promise<CreatorBattleRow[]> =>
      listIn(await staffApi<unknown>('/creator/battles', auth), 'battles', 'items').map(creatorRow).filter((x): x is CreatorBattleRow => Boolean(x)).sort((a, b) => b.createdAt - a.createdAt),
    end: (code: string) => staffApi<unknown>(`/creator/battles/${encodeURIComponent(code)}/end`, auth, { method: 'POST' }),
    results: async (code: string): Promise<BattleResultsRow[]> =>
      listIn(await staffApi<unknown>(`/creator/battles/${encodeURIComponent(code)}/results`, auth), 'results', 'players', 'rows', 'items')
        .map(resultsRow).filter((x): x is BattleResultsRow => Boolean(x)).sort((a, b) => a.rank - b.rank),
  };
}
export type CreatorClient = ReturnType<typeof creatorClient>;

/** GET /battles/open (public): open battles in the lobby or under way, newest first as the server sends them. */
export async function fetchOpenBattles(): Promise<OpenBattleRow[]> {
  return listIn(await api<unknown>('/battles/open'), 'battles', 'items').map(openRow).filter((x): x is OpenBattleRow => Boolean(x));
}

// ---------------------------------------------------------------------------
// Results as CSV, made in the browser
// ---------------------------------------------------------------------------
const cell = (v: string | number | undefined) => {
  const s = v === undefined ? '' : String(v);
  // Quote when needed, and keep a spreadsheet from reading a name as a formula.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};
export const RESULTS_HEADERS = ['Rank', 'Name', 'Score', 'Correct', 'Answered', 'Medical school', 'Year'] as const;
export function resultsCsv(rows: readonly BattleResultsRow[]): string {
  const lines = [RESULTS_HEADERS.join(','), ...rows.map((r) => [r.rank, r.name, r.score, r.correct, r.answered, r.medSchool, r.year].map(cell).join(','))];
  return `${lines.join('\r\n')}\r\n`;
}
/** Saves the CSV through a temporary link (nothing leaves the browser). */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// Host keys this device made (a convenience: My battles opens the host page with its key on the device that made it)
// ---------------------------------------------------------------------------
const HOST_KEYS = 'rounds.creator.hostKeys';
const KEEP = 50;
function readKeys(): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(HOST_KEYS) ?? '{}') as unknown;
    return obj(v) ? Object.fromEntries(Object.entries(v as Raw).filter(([, k]) => typeof k === 'string')) as Record<string, string> : {};
  } catch { return {}; }
}
export function rememberHostKey(code: string, secret: string): void {
  try {
    const keys = { ...readKeys(), [code]: secret };
    const kept = Object.entries(keys).slice(-KEEP);
    localStorage.setItem(HOST_KEYS, JSON.stringify(Object.fromEntries(kept)));
  } catch { /* storage refused: the created card still shows the host link */ }
}
export const hostKeyFor = (code: string): string | undefined => readKeys()[code];
